import React, { useState, useEffect, useCallback } from 'react';
import { fetchTrialBalance, exportReport } from '../api';

export default function TrialBalanceReport({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0, 10));
  const [startDate, setStartDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { as_at: asAt };
      if (startDate) params.start_date = startDate;
      const result = await fetchTrialBalance(authAxios, params);
      setData(result);
    } catch (e) {
      console.error('Failed to load trial balance', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, asAt, startDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const blob = await exportReport(authAxios, {
        report: 'trial-balance', format: fmt, as_at: asAt,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trial_balance_${asAt}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const fmt = (v) => v != null ? Number(v).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Trial Balance</h2>
        <div className="flex gap-2">
          <button onClick={() => handleExport('xlsx')} disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
            {exporting ? 'Exporting…' : 'Excel'}
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting}
            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-sm text-gray-600 mb-1">As At</label>
          <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Period Start (optional)</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Table */}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Account</th>
                <th className="text-left px-4 py-2">Category</th>
                {startDate && <th className="text-right px-4 py-2">Opening Dr</th>}
                {startDate && <th className="text-right px-4 py-2">Opening Cr</th>}
                <th className="text-right px-4 py-2">Debit</th>
                <th className="text-right px-4 py-2">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-1.5 font-mono">{r.code}</td>
                  <td className="px-4 py-1.5">{r.name}</td>
                  <td className="px-4 py-1.5 text-gray-500 text-xs">{r.category}</td>
                  {startDate && <td className="px-4 py-1.5 text-right">{fmt(r.opening_debit)}</td>}
                  {startDate && <td className="px-4 py-1.5 text-right">{fmt(r.opening_credit)}</td>}
                  <td className="px-4 py-1.5 text-right">{r.debit ? fmt(r.debit) : ''}</td>
                  <td className="px-4 py-1.5 text-right">{r.credit ? fmt(r.credit) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold bg-gray-50">
                <td colSpan={startDate ? 5 : 3} className="px-4 py-2 text-right">TOTAL</td>
                <td className="px-4 py-2 text-right">{fmt(data.total_debit)}</td>
                <td className="px-4 py-2 text-right">{fmt(data.total_credit)}</td>
              </tr>
            </tfoot>
          </table>
          {Math.abs(data.total_debit - data.total_credit) > 0.01 && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              Warning: Trial balance does not balance. Difference: {fmt(Math.abs(data.total_debit - data.total_credit))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
